import mapboxgl from "mapbox-gl";
import { stateToRegionByName, WELL_ZOOM_LEVELS as ZOOM_LEVELS } from "../../../../../constants/Map";
import { Dispatch } from "redux";
import { ICountyPublicWaterSystemResult } from "../../../../../types";
import { CountyActions } from "../../../../../store/County";
import { AmericanStatesActions } from "../../../../../store/AmericanStates";
import { removeMarkers } from "./handleMapMarkers";
import { WaterSystemGeoBlocksActions, WellWaterSystemsMetricsActions } from "../../../../../store/wellWaterSystem";
import { fetchWellAggregatedWaterSystems, getCoordinatesFromGeoBlock, getGeoBlocksFromCounty, getStateFromZoom } from "../services";

type HandleMoveEndProps = {
  mapRef: React.RefObject<mapboxgl.Map>;
  prevSelectedCode: React.MutableRefObject<string | null | undefined>;
  prevSelectedCounty: React.MutableRefObject<string | null| undefined>;
  prevSelectedGeoblock: React.MutableRefObject<string | null| undefined>;
  markersRef: React.RefObject<mapboxgl.Marker[]>;
  dispatch: Dispatch;
  countyHousingUnitsRef: React.RefObject<ICountyPublicWaterSystemResult[]>;
  hasDispatchedZoomOut: React.MutableRefObject<boolean>;
};

export const handleMoveEnd = async ({
  mapRef,
  prevSelectedCounty,
  prevSelectedCode,
  prevSelectedGeoblock,
  markersRef,
  dispatch,
  countyHousingUnitsRef,
  hasDispatchedZoomOut,
}: HandleMoveEndProps) => {
  const map = mapRef.current;
  if (!map) return;
  
  if (map.isZooming()) return;

  const zoom = map.getZoom();
  const center = map.getCenter();

  if (!zoom || !center) return;

  const isStateZoom = zoom >= ZOOM_LEVELS.STATE.MIN && zoom < ZOOM_LEVELS.STATE.MAX;
  const isCountyZoom = zoom >= ZOOM_LEVELS.COUNTY.MIN && zoom < ZOOM_LEVELS.COUNTY.MAX;
  const isGeoBlockZoom = zoom >= ZOOM_LEVELS.GEOBLKGROUP.MIN;

  // Reset state when zoomed out
  if (zoom < ZOOM_LEVELS.STATE.MIN) {
    if (!hasDispatchedZoomOut.current) {
      handleZoomOut(dispatch, markersRef, prevSelectedCode, prevSelectedCounty, prevSelectedGeoblock);
      dispatch(WellWaterSystemsMetricsActions.fetchWellWaterSystemMetricsAsync({ options:{} }) as any);
      hasDispatchedZoomOut.current = true;
    }
    return;
  }

  hasDispatchedZoomOut.current = false;

  try {
    
    const type = isCountyZoom ? 'place' : 'region';
    const features = await fetchGeocodingData(center, type);

    if (!features?.length) return;
    if (isCountyZoom && !isStateZoom) {
      handleCountyZoom(features, dispatch, countyHousingUnitsRef, prevSelectedCounty, prevSelectedCode, prevSelectedGeoblock, markersRef);
    } else if(isGeoBlockZoom && !isCountyZoom){
      const center = mapRef.current.getCenter();
    
      const point = map.project(center);
      const features = map.queryRenderedFeatures(point, {
        layers: ['maine'] 
      });
      if (features.length > 0) {
        const geoblockId = features[0].properties?.name;
        handleGeoBlockZoom(dispatch, geoblockId, prevSelectedGeoblock);
      }
    }else{
      handleStateZoom(features, dispatch, mapRef, prevSelectedCode, prevSelectedCounty, prevSelectedGeoblock, markersRef);
    }
  } catch (error) {
    console.error('Error fetching geocoding data:', error);
  }
};


const handleZoomOut = (
  dispatch: Dispatch,
  markersRef: React.RefObject<mapboxgl.Marker[]>,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>,
  prevSelectedGeoblock: React.MutableRefObject<string | null | undefined>
) => {
  removeMarkers(markersRef);
  dispatch(CountyActions.setSelectedCounty(''));
  dispatch(AmericanStatesActions.setSelectedState({}));
  prevSelectedCode.current = null;
  prevSelectedCounty.current = null;
  prevSelectedGeoblock.current = null;
};

const fetchGeocodingData = async (center: mapboxgl.LngLat, type: string) => {
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${center.lng},${center.lat}.json?access_token=${mapboxgl.accessToken}&types=${type}`
  );
  const data = await response.json();
  return data.features;
};

const handleCountyZoom = (
  features: any[],
  dispatch: Dispatch,
  countyHousingUnitsRef: React.RefObject<ICountyPublicWaterSystemResult[]>,
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  prevSelectedGeoblock: React.MutableRefObject<string | null | undefined>,
  markersRef: React.RefObject<mapboxgl.Marker[]>
) => {
  const county = features
    .flatMap(feature => feature.context)
    .filter(contextItem => contextItem?.id.startsWith('district'))
    .map(contextItem => contextItem.text.replace(' County', ''))[0];
    if (county && county !== prevSelectedCounty.current) {
      getGeoBlocksFromCounty(dispatch, countyHousingUnitsRef, county, prevSelectedCounty, prevSelectedCode, prevSelectedGeoblock);
      removeMarkers(markersRef);
  }
};

const handleGeoBlockZoom = (
  dispatch: Dispatch,
  geoblockId: string,
  prevSelectedGeoblock: React.MutableRefObject<string | null | undefined>
) => {

  if (geoblockId && geoblockId !== prevSelectedGeoblock.current) {
    getCoordinatesFromGeoBlock(dispatch, geoblockId, prevSelectedGeoblock);
  }
};

const handleStateZoom = (
  features: any[],
  dispatch: Dispatch,
  mapRef: React.RefObject<mapboxgl.Map>,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>,
  prevSelectedGeoblock: React.MutableRefObject<string | null | undefined>,
  markersRef: React.RefObject<mapboxgl.Marker[]>,
) => {
  const stateName = features[0].text;
  const { code } = stateToRegionByName[stateName] || {};

  if (stateName && code && code !== prevSelectedCode.current) {
    removeMarkers(markersRef);
    dispatch(WaterSystemGeoBlocksActions.setSelectedGeoBlock(''));
    getStateFromZoom(
      dispatch,
      mapRef,
      code,
      prevSelectedCode,
      prevSelectedCounty,
      prevSelectedGeoblock,
      fetchWellAggregatedWaterSystems,
      stateName
    );
  }
};
